import { all, takeEvery, call, put, select } from 'redux-saga/effects';
import { format, subDays } from 'date-fns';
import { convertToRaw } from 'draft-js';
import {
  ReservationsResponse,
  ReservationStatus,
} from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { FcfSettingsResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/fcf';

import URLS from 'constants/urls';
import { FCF_MODE } from 'constants/logic';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { transformKeysToCamelCase, transformKeysToSnakeCase } from 'helpers/core';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import {
  venueDetails,
  venuePagePhotos,
  venuePageAdditionalPhotos,
  venueDisabledTimeWindows,
  waivers,
  venueVideos,
} from 'store/saga/entities/bound';
import {
  selectActiveVenue,
  selectActiveVenueId,
  selectVenueByVenueId,
  selectRouteParams,
  selectVenueName,
  selectUserId,
  selectIaLogic,
} from 'selectors/general';
import { isFeatureEnabledForUser } from 'components/Optimizely/Optimizely.sdk';
import {
  OPTIMIZELY_FEATURE_FLAGS_KEYS,
  PARTNER_DASHBOARD_TRIAL_PROGRAM_MODE_EDIT,
} from 'components/Optimizely/Optimizely.features';
import { waitForModal } from 'components/Modal';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';

import { reportError } from 'helpers/error-logging';
import { selectEligibleVenuesForFcfBulkUpdate } from './VenuePage.selectors';
import { sagaMessages as messages, venueWaiversMessages } from './VenuePage.messages';
import { sagaMessages as disabledTimeWindowMessages } from './DisabledTimeWindows/DisabledTimeWindows.messages';

export const UPDATE_VENUE_PAGE_PHOTOS = 'venuePortal/venuePagePhotos/UPDATE_VENUE_PAGE_PHOTOS';
export const REDIRECT_HOME = 'venuePortal/venuePagePhotos/REDIRECT_HOME';
export const UPDATE_VENUE_PAGE_TEXT = 'venuePortal/venuePageText/UPDATE_VENUE_PAGE_TEXT';
export const UPDATE_VENUE_PAGE_SCHEDULE_URL = 'venuePortal/venuePageText/UPDATE_VENUE_PAGE_SCHEDULE_URL';
export const UPDATE_VENUE_PAGE_WAIVER = 'venuePortal/venuePageText/UPDATE_VENUE_PAGE_WAIVER';
export const UPDATE_VENUE_PAGE_WAIVER_ENABLED = 'venuePortal/venuePageText/UPDATE_VENUE_PAGE_WAIVER_ENABLED';
export const UPDATE_FCF_MODE = 'venuePortal/venuePage/UPDATE_FCF_MODE';
export const ADD_VENUE_PAGE_ADDITIONAL_PHOTO = 'venuePortal/venuePageAdditionalPhotos/ADD_VENUE_PAGE_ADDITIONAL_PHOTO';
export const DELETE_VENUE_PAGE_ADDITIONAL_PHOTO =
  'venuePortal/venuePageAdditionalPhotos/DELETE_VENUE_PAGE_ADDITIONAL_PHOTO';
export const SET_SELECTED_TAB = 'venuePortal/venuePage/SET_SELECTED_TAB';
export const REORDER_VENUE_PAGE_ADDITIONAL_PHOTO =
  'venuePortal/venuePageAdditionalPhotos/REORDER_VENUE_PAGE_ADDITIONAL_PHOTO';
export const CREATE_DISABLED_TIME_WINDOW = 'venuePortal/venuePage/CREATE_DISABLED_TIME_WINDOW';
export const DELETE_DISABLED_TIME_WINDOW = 'venuePortal/venuePage/DELETE_DISABLED_TIME_WINDOW';
export const DISMISS_VENUE_INTRO = 'venuePortal/venueDetails/DISMISS_VENUE_INTRO';
export const DISMISS_PHOTOS_INTRO = 'venuePortal/venueDetails/DISMISS_PHOTOS_INTRO';
export const SET_FCF_MODE_SELECTION_ENABLED = 'venuePortal/venuePage/SET_FCF_MODE_SELECTION_ENABLED';
export const SET_FCF_MODE_DATA = 'venuePortal/venuePage/SET_FCF_MODE_DATA';

export const updateVenuePagePhotos = (venueId, photosData) => ({ type: UPDATE_VENUE_PAGE_PHOTOS, venueId, photosData });
export const updateVenuePageText = (venueId, formValues, videoUpdates, formAsyncFns) => ({
  type: UPDATE_VENUE_PAGE_TEXT,
  venueId,
  formValues,
  formAsyncFns,
  videoUpdates,
});

export const updateVenuePageScheduleUrlAction = (venueId, formValues, formAsyncFns) => ({
  type: UPDATE_VENUE_PAGE_SCHEDULE_URL,
  venueId,
  formValues,
  formAsyncFns,
});

export const updateVenuePageWaiver = (venueId, formValues, formAsyncFns) => ({
  type: UPDATE_VENUE_PAGE_WAIVER,
  venueId,
  formValues,
  formAsyncFns,
});

export const updateVenuePageWaiverEnabled = (venueId, waiverId, formValues, formAsyncFns) => ({
  type: UPDATE_VENUE_PAGE_WAIVER_ENABLED,
  venueId,
  waiverId,
  formValues,
  formAsyncFns,
});

export const addVenuePageAdditionalPhoto = photoData => ({
  type: ADD_VENUE_PAGE_ADDITIONAL_PHOTO,
  photoData,
});

export const deleteVenuePageAdditionalPhoto = photoId => ({
  type: DELETE_VENUE_PAGE_ADDITIONAL_PHOTO,
  photoId,
});

export const updateFCFMode = (venueId, formValues, videoUpdates, formAsyncFns) => ({
  type: UPDATE_FCF_MODE,
  venueId,
  formValues,
  formAsyncFns,
  videoUpdates,
});

export const setSelectedTab = tab => ({ type: SET_SELECTED_TAB, tab });

export const reorderVenuePageAdditionalPhotos = photoIds => ({
  type: REORDER_VENUE_PAGE_ADDITIONAL_PHOTO,
  photoIds,
});

export const createDisabledTimeWindow = ({ timeWindowData, rejectSubmit, resolveSubmit, onCreated }) => ({
  type: CREATE_DISABLED_TIME_WINDOW,
  timeWindowData,
  rejectSubmit,
  resolveSubmit,
  onCreated,
});

export const deleteDisabledTimeWindow = ({ venueId, timeWindowId }) => ({
  type: DELETE_DISABLED_TIME_WINDOW,
  venueId,
  timeWindowId,
});

export const redirectHome = () => ({
  type: REDIRECT_HOME,
});

function* fetchFcfModeSelectionFlag() {
  try {
    const userId = yield select(selectUserId);
    const activeVenue = yield select(selectActiveVenue);

    const isEnabled = yield call(
      isFeatureEnabledForUser,
      OPTIMIZELY_FEATURE_FLAGS_KEYS[PARTNER_DASHBOARD_TRIAL_PROGRAM_MODE_EDIT],
      userId,
      { country_short_name: activeVenue.countryCodeShort }
    );
    yield put({ type: SET_FCF_MODE_SELECTION_ENABLED, payload: isEnabled });
  } catch (error) {
    yield put({ type: SET_FCF_MODE_SELECTION_ENABLED, payload: false });
    yield call(reportError, error);
  }
}

export function* fetchFcfModeSelectionData(dependencies, venueId) {
  try {
    const { userHasAccess } = yield select(selectIaLogic, IA_KEY.MANAGE_FCF_MODE);

    if (!userHasAccess) {
      yield put({
        type: SET_FCF_MODE_DATA,
        payload: { canShowFcfSection: false, canBulkUpdate: false, eligibleManagedVenues: [] },
      });
      return;
    }

    const currentSettingsResponse = yield call(
      [dependencies.httpClient, dependencies.httpClient.get],
      URLS.venue.fcf.getSettings(venueId)
    );

    const currentSettingsData =
      transformKeysToCamelCase(FcfSettingsResponse.fromJSON(currentSettingsResponse.data)) || {};

    yield put({ type: SET_FCF_MODE_DATA, payload: currentSettingsData });
  } catch (error) {
    yield put({
      type: SET_FCF_MODE_DATA,
      payload: { canShowFcfSection: false, canBulkUpdate: false, eligibleManagedVenues: [] },
    });
    yield call(reportError, error);
  }
}

export function* handleVenueRouteEnter(dependencies) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const { tab } = yield select(selectRouteParams);

    if (!tab) {
      yield call(dependencies.navigate, makePath.editVenue(venueId, 'profile'));
    }
    yield all([
      call(fetchEntityIfNeeded, venueDetails, [dependencies, venueId]),
      call(fetchEntityIfNeeded, venuePagePhotos, [dependencies, venueId]),
      call(fetchEntityIfNeeded, venuePageAdditionalPhotos, [dependencies, venueId]),
      call(fetchEntityIfNeeded, waivers, [dependencies, waivers.method.latest, venueId]),
      call(fetchEntityIfNeeded, venueDisabledTimeWindows, [dependencies, venueId]),
      call(fetchEntityIfNeeded, venueVideos, [dependencies, venueVideos.method.read, venueId]),
      call(fetchFcfModeSelectionFlag),
      call(fetchFcfModeSelectionData, dependencies, venueId),
    ]);
  } catch (error) {
    yield all([
      put(createErrorBanner(messages.requestFailure)),
      call(dependencies.navigate, makePath.errorPage()),
      call(reportError, error),
    ]);
  }
}

export function* handleActiveVenueChange(dependencies, action) {
  const { venueId } = action;
  const { tab } = yield select(selectRouteParams);
  yield call(dependencies.navigate, makePath.editVenue(venueId, tab));

  yield all([
    call(fetchEntityIfNeeded, venueDetails, [dependencies, venueId]),
    call(fetchEntityIfNeeded, venuePagePhotos, [dependencies, venueId]),
    call(fetchEntityIfNeeded, venuePageAdditionalPhotos, [dependencies, venueId]),
    call(fetchEntityIfNeeded, waivers, [dependencies, waivers.method.latest, venueId]),
    call(fetchEntityIfNeeded, venueDisabledTimeWindows, [dependencies, venueId]),
    call(fetchEntityIfNeeded, venueVideos, [dependencies, venueVideos.method.read, venueId]),
    call(fetchFcfModeSelectionFlag),
    call(fetchFcfModeSelectionData, dependencies, venueId),
  ]);
}

export function* doUpdateVenuePagePhotos(dependencies, action) {
  try {
    const { venueId, photosData } = action;
    const { photoType } = photosData;
    const data = yield call(fetchEntity, venuePagePhotos, [dependencies, venueId, photosData]);
    yield call(Segment.event, 'Upload Photo', { venueId, photoType: photoType === 'logo' ? 'Logo' : 'Cover' });
    // This is needed to update the photo data on the page
    yield put(venuePagePhotos.successAction({ data, key: venueId }));
    yield all([put(createInfoBanner(messages.photosSuccess)), call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.PHOTO_ADDED)]);
  } catch (error) {
    yield put(createErrorBanner(messages.photosFailure));
  }
}

export function* doAddVenuePageAdditionalPhoto(dependencies, action) {
  try {
    const { photoData } = action;
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, venuePageAdditionalPhotos, [dependencies, venueId, photoData]);
    yield all([
      put(createInfoBanner(messages.photosSuccess)),
      call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.PHOTO_ADDED),
      call(Segment.event, 'Upload Photo', { venueId, photoType: 'Additional' }),
    ]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.photosFailure)), call(reportError, error)]);
  }
}

export function* doReorderVenuePageAdditionalPhoto(dependencies, action) {
  try {
    const { photoIds } = action;
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, venuePageAdditionalPhotos, [dependencies, venueId, { mediaOrder: photoIds }]);
    yield put(createInfoBanner(messages.reorderPhotosSuccess));
  } catch (error) {
    yield all([put(createErrorBanner(messages.reorderPhotosFailure)), call(reportError, error)]);
  }
}

export function* doDeleteVenuePageAdditionalPhoto(dependencies, action) {
  try {
    const { photoId } = action;
    const { httpClient } = dependencies;

    const venueId = yield select(selectActiveVenueId);

    const confirmed = yield call(waitForModal, dependencies, 'AdditionalPhotoDeleteConfirmModal');

    if (confirmed) {
      yield call([httpClient, httpClient.delete], URLS.venue.carouselPhotosDelete(venueId, photoId));
      yield call(fetchEntity, venuePageAdditionalPhotos, [dependencies, venueId]);
      yield put(createInfoBanner(messages.additionalPhotoDeleteSuccess));
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.additionalPhotoDeleteFailure)), call(reportError, error)]);
  }
}

export function* doUpdateVenuePageScheduleUrl(dependencies, action) {
  const { venueId, formValues, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    yield call(fetchEntity, venueDetails, [dependencies, venueId, formValues]);
    yield all([
      call(resolveSubmit),
      put(createInfoBanner(messages.textSuccess)),
      call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.AUTOSYNC_URL_ADDED),
    ]);
  } catch (err) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.textFailure))]);
  }
}

export function* doUpdateVenuePageText(dependencies, action) {
  const { venueId, formValues, videoUpdates, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    yield all([
      call(fetchEntity, venueVideos, [dependencies, venueVideos.method.batch, venueId, videoUpdates]),
      call(fetchEntity, venueDetails, [dependencies, venueId, formValues]),
    ]);

    yield all([call(resolveSubmit), put(createInfoBanner(messages.textSuccess))]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.BUSINESS_PROFILE_UPDATED);
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.textFailure))]);
  }
}

export function* doUpdateVenuePageWaiverEnabled(dependencies, action) {
  const {
    venueId,
    waiverId,
    formValues: { isEnabled },
    formAsyncFns: { resolveSubmit, rejectSubmit },
  } = action;
  const venueName = yield select(selectVenueName);
  try {
    yield call(fetchEntity, waivers, [dependencies, waivers.method.toggle, venueId, waiverId, isEnabled]);
    yield all([
      call(resolveSubmit),
      put(
        createInfoBanner(isEnabled ? venueWaiversMessages.enableSuccess : venueWaiversMessages.disableSuccess, {
          venueName,
        })
      ),
    ]);
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.textFailure, { venueName }))]);
  }
}

export function* doUpdateVenuePageWaiver(dependencies, action) {
  const {
    venueId,
    formValues: { isEnabled, waiver: waiverEditorState },
    formAsyncFns: { resolveSubmit, rejectSubmit },
  } = action;
  const currentContent = yield call([waiverEditorState, waiverEditorState.getCurrentContent]);
  const waiverDataState = yield call(convertToRaw, currentContent);
  const waiverData = yield call(JSON.stringify, waiverDataState);
  const payload = { isEnabled: typeof isEnabled === 'undefined' || isEnabled, waiverData };

  try {
    yield call(fetchEntity, waivers, [dependencies, waivers.method.create, venueId, payload]);
    yield all([call(resolveSubmit), put(createInfoBanner(messages.textSuccess))]);
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.textFailure))]);
  }
}

function* updateSingleVenueFCFMode(httpClient, venueId, formValues) {
  yield call(
    [httpClient, httpClient.put],
    URLS.venue.fcf.updateSettings(venueId),
    transformKeysToSnakeCase({
      fcfMode: FCF_MODE[formValues.currentVenueFcfMode],
    })
  );
}

function* updateBulkVenueFCFMode(httpClient, venueId, formValues) {
  yield call(
    [httpClient, httpClient.put],
    URLS.venue.fcf.bulkUpdateSettings,
    transformKeysToSnakeCase({
      fcfMode: FCF_MODE[formValues.currentVenueFcfMode],
      venueId,
    })
  );
}

export function* doUpdateFCFMode(dependencies, action) {
  const { httpClient } = dependencies;
  const { venueId, formValues, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;
  const updatedTo = FCF_MODE[formValues.currentVenueFcfMode];

  try {
    const dashboardUserId = yield select(selectUserId);

    if (formValues.fcfModeApplyToAllVenues) {
      const confirmed = yield call(waitForModal, dependencies, 'BulkFCFModeConfirmModal');
      if (!confirmed) {
        yield call(rejectSubmit);
        return;
      }
      yield call(updateBulkVenueFCFMode, httpClient, venueId, formValues);
      const eligibleVenues = yield select(selectEligibleVenuesForFcfBulkUpdate);
      yield call(Segment.event, 'FCF version bulk updated', {
        venuesIds: eligibleVenues.map(v => v.venueId),
        updatedTo,
        dashboardUserId,
      });
    } else {
      yield call(updateSingleVenueFCFMode, httpClient, venueId, formValues);
      yield call(Segment.event, 'FCF version updated', {
        venueId,
        updatedTo,
        dashboardUserId,
      });
    }

    yield all([
      call(fetchFcfModeSelectionData, dependencies, venueId),
      call(resolveSubmit),
      put(createInfoBanner(messages.textSuccess)),
    ]);
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.textFailure))]);
  }
}

export function* doSetSelectedTab(dependencies, action) {
  const { tab } = action;
  const venueId = yield select(selectActiveVenueId);
  const newPath = makePath.editVenue(venueId, tab);
  yield call(dependencies.navigate, newPath);
}

export function* doCreateDisabledTimeWindow(dependencies, { timeWindowData, rejectSubmit, resolveSubmit, onCreated }) {
  const { httpClient } = dependencies;
  const { venueId, startTime, endTime } = timeWindowData;

  const { timezone } = yield select(selectVenueByVenueId, venueId);
  const encodedStartDate = format(adjustDateForTimezone(startTime, timezone), 'yyyy-MM-dd');
  const encodedEndDate = format(subDays(adjustDateForTimezone(endTime, timezone), 1), 'yyyy-MM-dd');

  try {
    const numReservationsResponse = yield call(
      [httpClient, httpClient.get],
      URLS.venue.reservations({ venueId, startDate: encodedStartDate, endDate: encodedEndDate })
    );
    const { reservations } = transformKeysToCamelCase(ReservationsResponse.fromJSON(numReservationsResponse.data));
    const numProcessedReservations = reservations.filter(
      reservation => reservation.reservationStatus === ReservationStatus.PROCESSED
    ).length;

    // Only show the confirmation modal if there are reservations
    let confirmed = true;
    if (numProcessedReservations > 0) {
      confirmed = yield call(waitForModal, dependencies, 'CreateDisabledTimeWindowConfirmModal', {
        numReservations: numProcessedReservations,
      });
    }

    if (confirmed) {
      yield call(
        [httpClient, httpClient.post],
        URLS.venue.createDisabledTimeWindow({ venueId }),
        transformKeysToSnakeCase(timeWindowData)
      );
      yield call(fetchEntity, venueDisabledTimeWindows, [dependencies, venueId]);
      yield all([
        call(onCreated),
        call(waitForModal, dependencies, 'SuccessDisabledTimeWindowModal'),
        call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.ADDED_CLOSED_DATES),
      ]);
    }
    yield call(resolveSubmit);
  } catch (error) {
    yield call(rejectSubmit(disabledTimeWindowMessages.createDisabledTimeWindowFailure));
  }
}

export function* doDeleteDisabledTimeWindow(dependencies, { venueId, timeWindowId }) {
  const { httpClient } = dependencies;
  try {
    const confirmed = yield call(waitForModal, dependencies, 'DeleteDisabledTimeWindowConfirmModal');

    if (confirmed) {
      yield call([httpClient, httpClient.delete], URLS.venue.deleteDisabledTimeWindow({ venueId, timeWindowId }));
      yield call(fetchEntity, venueDisabledTimeWindows, [dependencies, venueId]);
    }
  } catch (error) {
    yield put(createErrorBanner(disabledTimeWindowMessages.deleteDisabledTimeWindowFailure));
  }
}

export function* doRedirectHome(dependencies) {
  try {
    const venueId = yield select(selectActiveVenueId);
    yield call(dependencies.navigate, makePath.home(venueId));
  } catch (error) {
    yield put(createErrorBanner(messages.redirectFailure));
  }
}

export function* watchVenuePage(dependencies) {
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, dependencies);
  yield takeEvery(UPDATE_VENUE_PAGE_PHOTOS, doUpdateVenuePagePhotos, dependencies);
  yield takeEvery(UPDATE_VENUE_PAGE_TEXT, doUpdateVenuePageText, dependencies);
  yield takeEvery(UPDATE_VENUE_PAGE_SCHEDULE_URL, doUpdateVenuePageScheduleUrl, dependencies);
  yield takeEvery(UPDATE_VENUE_PAGE_WAIVER, doUpdateVenuePageWaiver, dependencies);
  yield takeEvery(UPDATE_VENUE_PAGE_WAIVER_ENABLED, doUpdateVenuePageWaiverEnabled, dependencies);
  yield takeEvery(UPDATE_FCF_MODE, doUpdateFCFMode, dependencies);
  yield takeEvery(ADD_VENUE_PAGE_ADDITIONAL_PHOTO, doAddVenuePageAdditionalPhoto, dependencies);
  yield takeEvery(DELETE_VENUE_PAGE_ADDITIONAL_PHOTO, doDeleteVenuePageAdditionalPhoto, dependencies);
  yield takeEvery(SET_SELECTED_TAB, doSetSelectedTab, dependencies);
  yield takeEvery(REORDER_VENUE_PAGE_ADDITIONAL_PHOTO, doReorderVenuePageAdditionalPhoto, dependencies);
  yield takeEvery(CREATE_DISABLED_TIME_WINDOW, doCreateDisabledTimeWindow, dependencies);
  yield takeEvery(DELETE_DISABLED_TIME_WINDOW, doDeleteDisabledTimeWindow, dependencies);
  yield takeEvery(REDIRECT_HOME, doRedirectHome, dependencies);
}
